import React, { useState } from 'react';
import { Modal, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { Service } from '../types';
import { useServices, useFormSubmission } from '../hooks/useApi';

interface BookingModalProps {
  show: boolean;
  onHide: () => void;
  selectedService?: Service;
}

const BookingModal: React.FC<BookingModalProps> = ({ show, onHide, selectedService }) => {
  const { services, fetchServices } = useServices();
  const { loading, error, success, submitBooking, reset } = useFormSubmission();
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: selectedService?.title || selectedService?.service_name || '',
    preferred_date: '',
    preferred_time: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitBooking(formData);
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  const handleShow = () => {
    fetchServices();
  };

  return (
    <Modal show={show} onHide={handleClose} onShow={handleShow} size="lg" centered>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>Book Your Service</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {success ? (
          <Alert variant="success" className="text-center">
            <h4 className="fw-bold mb-3">Booking Confirmed!</h4>
            <p>We will contact you shortly to confirm your appointment.</p>
            <Button variant="primary" onClick={handleClose}>Close</Button>
          </Alert>
        ) : (
          <Form onSubmit={handleSubmit}>
            {error && <Alert variant="danger">{error}</Alert>}
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number *</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Service Required *</Form.Label>
              <Form.Select
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
              >
                <option value="">Select a service</option>
                {services.map((service) => (
                  <option key={service.id} value={service.title || service.service_name}>
                    {service.title || service.service_name} - ₹{service.price || service.starting_price}+
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Preferred Date *</Form.Label>
                  <Form.Control
                    type="date"
                    name="preferred_date"
                    value={formData.preferred_date}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Preferred Time *</Form.Label>
                  <Form.Control
                    type="time"
                    name="preferred_time"
                    value={formData.preferred_time}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label>Additional Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="message"
                value={formData.message}
                onChange={handleChange}
              />
            </Form.Group>

            <div className="d-flex gap-3">
              <Button variant="secondary" onClick={handleClose} className="flex-fill">
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                className="flex-fill"
                disabled={loading}
              >
                {loading ? 'Booking...' : 'Book Service Now'}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default BookingModal;